import { Component, Renderer2, Input, EventEmitter, Output, forwardRef, ElementRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, FormControl, Validator } from '@angular/forms';

@Component({
    selector: "phonenumber-input",
    templateUrl: "./phonenumber-input.component.html",
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneNumberInputComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PhoneNumberInputComponent),
            multi: true,
        }
    ],
    standalone: false
})
export class PhoneNumberInputComponent implements ControlValueAccessor, Validator  {
    static componentName = "PhoneNumberInputComponent";

    inputMask = "+1 (###) ###-####"; //TODO: get input mask from country - hard-coding for US/Canada for now
    phoneNumberValue: string = '';
    _isDisabled: boolean;
    _textIsSelected: boolean = false;
    _selectedTextEvent: any;

    constructor(public renderer: Renderer2, public elementRef: ElementRef) {
    }

    ngOnInit() {
    }


    @Input() set isDisabled(value: boolean) {
        this._isDisabled = value;
    }

    get isDisabled(): boolean {
        return this._isDisabled;
    }

    get phoneNumber(): string {
        return this.phoneNumberValue;
    }

    set phoneNumber(value: string) {
        this.phoneNumberValue = this.formatInput(value);
        this.propagateChange(this.phoneNumberValue);
    }

    checkInput(event: KeyboardEvent) {
        const key = event.key;
        if (!this.validateEntry(key, event)) {
            event.preventDefault();
            return false;
        }
        else {
            //If it's a valid character, make sure we reformat it.
            let formattedValue = this.formatInput(this.phoneNumberValue);
            this.phoneNumberValue = formattedValue;
            this.propagateChange(this.phoneNumberValue);
        }
        this._textIsSelected = false;
    }

    formatInput(input: string): string {
        const format = this.inputMask;
        let inputIndex = 0;
        let result = "";
        if (input && input.length > 0) {

            //For most of the special characters, they can only exist in certain spots, so if we need to add one, remove any existing ones first
            for (let f = 0; f < format.length; f++) {
                const formatChar = format[f];

                switch (formatChar) {
                    case "+":
                        input = input.replace("+", "");
                    case "(":
                        input = input.replace("(", "");
                        result = `${result}${formatChar}`;
                        if (input[inputIndex] === formatChar) {

                            inputIndex = inputIndex + 1;
                        }
                        break;
                    case " ":
                        //Replace the multiple occurences
                        input = input.split(" ").join("");;
                    case "-":
                        input = input.replace("-", "");
                    case ")":
                        input = input.replace(")", "");

                        result = `${result}${formatChar}`;
                        if (input[inputIndex] === formatChar) {
                            inputIndex = inputIndex + 1;
                        }
                        break;
                    case "#":
                        input = input.replace("#", "");
                        result = `${result}${input[inputIndex]}`;
                        inputIndex = inputIndex + 1;
                        break;
                    default:
                        const charAsNumber = parseInt(formatChar, 10);
                        if (isNaN(charAsNumber)) continue;
                        result = `${result}${formatChar}`;
                        if (input[inputIndex] === formatChar) {

                            inputIndex = inputIndex + 1;
                        }
                        break;
                }
                if (inputIndex >= input.length) break;
            }
        }
        return result;
    }

    onBlur(event: any) {
        let formattedValue = this.formatInput(this.phoneNumberValue);
        this.phoneNumberValue = formattedValue;
        this.propagateChange(this.phoneNumberValue);
    }

    onClick(event: any) {
        this._textIsSelected = false;
    }

    onKeyUp(event: KeyboardEvent) {
        const key = event.key;
        if (this.validateEntry(key, event)) {
            //If it's a valid character, make sure we reformat it.
            let formattedValue = this.formatInput(this.phoneNumberValue);
            this.phoneNumberValue = formattedValue;
            this.propagateChange(this.phoneNumberValue);
            return true;
        }
        event.preventDefault();
        this._textIsSelected = false;
        return false;
    }

    onTextSelected(event: any) {
        this._textIsSelected = true;
        this._selectedTextEvent = event;
    }

    phonePasted(event: any): void {
        let formattedValue: string = this.phoneNumberValue;
        const pastedText = event.clipboardData.getData("text");
        let validPastedText = "";

        for (let c = 0; c < pastedText.length; c++) {
            if (this.validateEntry(pastedText[c], false)) { //filter the pasted text for valid characters
                validPastedText = `${validPastedText}${pastedText[c]}`;
            }
        }

        let selectedTextLength = 0;
        if (this._textIsSelected) {
            //Cut out the text we've selected since they're pasting something else in.
            let startingindex: number = this._selectedTextEvent.target.selectionStart;
            let endingindex: number = this._selectedTextEvent.target.selectionEnd;
            let selectedText: string = this._selectedTextEvent.target.value.substring(startingindex, endingindex);
            selectedTextLength = selectedText.length;

            formattedValue = formattedValue.substring(0, event.target.selectionStart) + formattedValue.substring(event.target.selectionEnd);
        }
        else {
            var amountOverLength = (formattedValue.length + validPastedText.length) - this.inputMask.length;
        }

        //Check if we're over length
        var amountOverLength = (formattedValue.length - selectedTextLength + validPastedText.length) - this.inputMask.length;

        //We're over the length, trim that many characters off pasted text
        if (amountOverLength > 0) {
            validPastedText = validPastedText.substring(0, validPastedText.length - amountOverLength);
        }

        formattedValue = formattedValue.substring(0, event.target.selectionStart) + validPastedText + formattedValue.substring(event.target.selectionStart);

        formattedValue = this.formatInput(formattedValue);
        this.phoneNumberValue = formattedValue;
        this.propagateChange(this.phoneNumberValue);
        event.preventDefault();
    }

    validateEntry(entry: string, event: any, checkAgainstPhoneNumberLength: boolean = true) {
        if ((!checkAgainstPhoneNumberLength || this._textIsSelected || (checkAgainstPhoneNumberLength && this.phoneNumber.length < this.inputMask.length && !this._textIsSelected))) {

            //If they're the special characters, make sure we're trying to insert them at a valid spot in the mask
            if ((entry === "-" || entry === "(" || entry === ")" || entry === "+" || entry === "." || entry === " ")
                && event.target && this.inputMask[event.target.selectionStart] == entry)
                return true;

            const entryAsNumber = parseInt(entry, 10);
            if (!isNaN(entryAsNumber)) {
                return true;
            }
        }
        return false;
    }

    writeValue(obj: any): void {
        if (obj) {
            this.phoneNumber = this.formatInput(obj);
        }
    }

    // the method set in registerOnChange to emit changes back to the form
    public propagateChange = (_: any) => {  };
    public propagateTouched = (_: any) => { };

    registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.propagateTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._isDisabled = isDisabled;
        this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    }

    // validates the form
    public validate(c: FormControl) {
        return null;
    }
}
